import React, { useCallback, useEffect, useState } from "react"
import { Button, Card, CardBody, Col, Input, Label, Row, Spinner } from "reactstrap"
import Cookies from "js-cookie"

const sectionHeadingStyle = {
  fontSize: "14px",
  fontWeight: "600",
  borderBottom: "2px solid #EE5A29",
  paddingBottom: "6px",
  display: "inline-block",
  marginBottom: "20px",
  color: "#EE5A29",
}

// The tenant's own mail domains (qd_customer.email_domain). Stored as a single
// comma-separated value; edited here as one row per domain. An empty list is a
// deliberate clear — the server writes NULL, which turns the filtering off.
function EmailDomainsEditor({ scac }) {
  const [domains, setDomains] = useState([])
  const [found, setFound] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  const api_url = process.env.REACT_APP_API_URL
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${Cookies.get("idToken")}`,
  })

  // Note: this deliberately leaves `msg` alone — handleSave re-loads after a
  // successful write and the "Saved" message has to survive that refresh.
  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(
          scac
        )}&section=email_domains`,
        { method: "GET", headers: authHeaders(), credentials: "include" }
      )
      if (!res.ok) {
        setLoadError("Could not load email domains for this tenant.")
        return
      }
      const data = await res.json()
      setFound(!!(data && data.found))
      // No stored domains means no rows at all — a row only appears via "+ Add
      // domain", so the empty-row placeholder can never read as a stored value.
      setDomains((data && data.email_domains) || [])
    } catch (err) {
      console.error("Failed to load email domains:", err)
      setLoadError("Could not load email domains for this tenant.")
    }
  }, [scac, api_url])

  useEffect(() => {
    load()
  }, [load])

  const handleChange = (idx, value) => {
    setDomains(prev => prev.map((d, i) => (i === idx ? value : d)))
    setMsg(null)
  }

  const handleRemove = idx => {
    setDomains(prev => prev.filter((_, i) => i !== idx))
    setMsg(null)
  }

  const handleAdd = () => {
    setDomains(prev => [...prev, ""])
    setMsg(null)
  }

  const handleSave = async () => {
    setSaving(true)
    setMsg(null)
    try {
      // Blank rows are dropped server-side; sending them keeps this simple.
      const res = await fetch(
        `${api_url}crs/tenant-onboarding?scac=${encodeURIComponent(
          scac
        )}&section=email_domains`,
        {
          method: "PUT",
          headers: authHeaders(),
          credentials: "include",
          body: JSON.stringify({ email_domains: domains }),
        }
      )
      if (!res.ok)
        throw new Error(`Request failed: ${res.status} ${res.statusText}`)
      const data = await res.json()
      const ok =
        Array.isArray(data) &&
        data.length > 0 &&
        data.every(r => r.status === "success")
      const firstError = Array.isArray(data)
        ? data.find(r => r.status !== "success")
        : null
      setMsg({
        text: ok
          ? (data[0] && data[0].message) || "Saved"
          : (firstError && firstError.message) || "Save failed",
        ok,
      })
      if (ok) await load()
    } catch (err) {
      setMsg({ text: err.message, ok: false })
    } finally {
      setSaving(false)
    }
  }

  if (loadError) {
    return (
      <div className="alert alert-warning" style={{ fontSize: "13px" }}>
        {loadError}
      </div>
    )
  }

  // Nothing to show until the first load resolves, or if the SCAC isn't onboarded
  // (the Results card above already reports that).
  if (!found) return null

  return (
    <Card className="shadow-sm">
      <CardBody className="p-3">
        <h6 style={sectionHeadingStyle}>Email Domains</h6>

        {domains.length === 0 && (
          <Row className="mb-2">
            <Label className="col-sm-3 col-form-label">Domains</Label>
            <Col sm={7}>
              <div
                className="text-muted"
                style={{ paddingTop: "7px", fontSize: "13px" }}
              >
                None configured.
              </div>
            </Col>
          </Row>
        )}

        {domains.map((domain, idx) => (
          <Row className="mb-2" key={idx}>
            <Label
              htmlFor={`email-domain-${idx}`}
              className="col-sm-3 col-form-label"
            >
              {idx === 0 ? "Domains" : ""}
            </Label>
            <Col sm={7}>
              <div className="d-flex">
                <Input
                  id={`email-domain-${idx}`}
                  type="text"
                  value={domain}
                  onChange={e => handleChange(idx, e.target.value)}
                  placeholder="example.com"
                  style={{
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                />
                <Button
                  type="button"
                  color="light"
                  onClick={() => handleRemove(idx)}
                  aria-label={`Remove domain ${idx + 1}`}
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    border: "1px solid #ced4da",
                    color: "#495057",
                    whiteSpace: "nowrap",
                  }}
                >
                  Remove
                </Button>
              </div>
            </Col>
          </Row>
        ))}

        <Row className="mb-3">
          <Col sm={{ offset: 3 }}>
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={handleAdd}
            >
              + Add domain
            </button>
          </Col>
        </Row>

        <Row>
          <Col sm={{ offset: 3, size: 7 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Spinner size="sm" /> : "Save"}
            </button>
            {msg && (
              <span
                className="ms-3"
                style={{
                  fontSize: "13px",
                  color: msg.ok ? "#1a7a55" : "#c0392b",
                }}
              >
                {msg.text}
              </span>
            )}
            <div className="text-muted mt-2" style={{ fontSize: "12px" }}>
              The customer&apos;s own mail domains. Recipients at these domains
              are excluded from appointment request emails. Enter the bare domain
              (e.g. <code>example.com</code>); remove every row and save to turn
              the filtering off.
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

export default EmailDomainsEditor
